import { willTaskReceiveAutomaticRefundOnClose } from '@frontend/api-client';
import type { Task } from '@frontend/api-types';
import type { TaskNotDoneReason } from '@frontend/constants';
import {
  TASK_MEMBER_CONFIRMATION_STATUS_OPTIONS,
  TASK_NOT_DONE_REASONS,
  TASK_NOT_DONE_REASON_ICON_MAP,
  colors,
} from '@frontend/constants';
import type { StrictOmit } from '@frontend/duck-tape';
import { isLast, match, pluralize } from '@frontend/duck-tape';
import { useFeatureIsOn } from '@frontend/react';
import { useCloseTaskFlow } from '@frontend/react-domain';
import type { CommonModalProps } from '@frontend/web-react';
import {
  Button,
  Divider,
  Icon,
  Modal,
  RatingOptions,
  Text,
  TextareaInput,
  XStack,
  YStack,
  showToast,
} from '@frontend/web-react';
import type { IconName } from '@frontend/web-react/components/Primitives/Icon/helpers';
import { usePricingTerminology } from '@frontend/web/context';
import { useCloseTaskMutation, useConfirmDoneMutation, useCreateTaskReviewMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useMemo } from 'react';

type CloseTaskModalProps = CommonModalProps & {
  task: StrictOmit<Task, 'zendeskId'>;
};

const incompleteReasonIconMap = TASK_NOT_DONE_REASON_ICON_MAP satisfies Record<TaskNotDoneReason, IconName>;

export const CloseTaskModal = ({ isOpen, onClose, task }: CloseTaskModalProps) => {
  const [closeTaskMutation, { isLoading: isCloseTaskLoading }] = useCloseTaskMutation();
  const [createReviewMutation, { isLoading: isCreateTaskReviewLoading }] = useCreateTaskReviewMutation();
  const [confirmDoneStatusMutation, { isLoading: isConfirmDoneLoading }] = useConfirmDoneMutation();
  const { pricingTerminology, showCredits } = usePricingTerminology();
  const { enabled: isRecurrencesRevampedEnabled } = useFeatureIsOn('is_recurrences_revamped_enabled');

  const {
    canGoBack,
    closeAndResetState,
    handleBackClick,
    handleSubmitClick,
    isSubmitDisabled,
    isSubmitLoading,
    onAcknowledgeRecurringWarning,
    onSelectConfirmationStatus,
    onSelectNotDoneReason,
    onSkipNotDoneReason,
    onUpdateFeedback,
    onUpdateRating,
    state,
    title,
  } = useCloseTaskFlow({
    closeDialog: onClose,
    closeTask: closeTaskMutation,
    confirmDoneStatus: confirmDoneStatusMutation,
    createReview: createReviewMutation,
    handleMutation,
    isCloseTaskLoading,
    isConfirmDoneLoading,
    isCreateTaskReviewLoading,
    isRecurrencesRevampedEnabled,
    showToast: ({ backgroundColor, title }) =>
      showToast({ title, variant: backgroundColor === 'green' ? 'success' : 'neutral' }),
    task,
  });

  const centerContent = useMemo(
    () =>
      match(state.step)
        .with('recurringWarning', () => (
          <YStack className="p-md">
            <YStack className="bg-red-50 p-md rounded-md">
              <Text className="text-red-800" type="p-sm">
                Warning: Closing this task will end all future recurrence instances.
              </Text>
            </YStack>
          </YStack>
        ))
        .with('selectConfirmationStatus', () => (
          <YStack pb="md">
            {willTaskReceiveAutomaticRefundOnClose(task) ? (
              <XStack bg="blue50" gapX="sm" mt="md" mx="md" p="md" radius="md">
                {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
                <Text span type="p-sm">
                  💡
                </Text>
                <YStack flexShrink={1}>
                  <Text type="p-sm">
                    {showCredits
                      ? `Since this task hasn't started yet, you'll receive an automatic refund for ${task.creditCost} ${pluralize(pricingTerminology.unit_term, task.creditCost)} shortly after you close it.`
                      : "Since this task hasn't started yet, you'll receive an automatic refund shortly after you close it."}
                  </Text>
                </YStack>
              </XStack>
            ) : null}
            {TASK_MEMBER_CONFIRMATION_STATUS_OPTIONS.map((r) => {
              const isSelected = state.confirmationStatus && r.value === state.confirmationStatus;
              return (
                <XStack
                  alignItems="center"
                  enableHoverEffect
                  gapX="md"
                  key={r.id}
                  onClick={() => onSelectConfirmationStatus(r.value)}
                  p="md"
                  radius="md"
                >
                  <YStack className="flex-1">
                    <Text type="h5">{r.label}</Text>
                    <Text color="secondary" type="p-sm">
                      {r.description}
                    </Text>
                  </YStack>
                  <Icon
                    colorOverride={isSelected ? colors.textSuccessDisabled : colors.borderPrimary}
                    name={isSelected ? 'IconCircleCheckFilled' : 'IconCircle'}
                    size={24}
                  />
                </XStack>
              );
            })}
          </YStack>
        ))
        .with('incompleteReason', () => (
          <YStack>
            {TASK_NOT_DONE_REASONS.map((notDoneReason, index, array) => {
              return (
                <YStack key={notDoneReason}>
                  <XStack
                    className="items-center gap-x-md p-md cursor-pointer hover:bg-slate-50 rounded-md"
                    onClick={() => onSelectNotDoneReason(notDoneReason)}
                  >
                    <Icon name={incompleteReasonIconMap[notDoneReason]} size={18} />
                    <Text>{notDoneReason}</Text>
                  </XStack>
                  {!isLast(array, index) ? <Divider className="mx-md" /> : null}
                </YStack>
              );
            })}
          </YStack>
        ))
        .with('taskReview', () => (
          <YStack className="gap-y-lg px-md py-lg">
            {state.notDoneReason ? (
              <XStack className="items-center gap-x-sm">
                <Text type="h6">{state.notDoneReason}</Text>
              </XStack>
            ) : null}
            <RatingOptions onChange={onUpdateRating} value={state.rating} />
            <YStack>
              <YStack gapY="md">
                <Text type="h5">Tell us more so we can improve</Text>
                <TextareaInput onChange={onUpdateFeedback} value={state.feedback} />
              </YStack>
            </YStack>
          </YStack>
        ))
        .exhaustive(),
    [state.confirmationStatus, state.feedback, state.notDoneReason, state.rating, state.step],
  );

  const footerButtons = match(state.step)
    .with('recurringWarning', () => (
      <XStack className="gap-md">
        <Button label="Nevermind" onClick={closeAndResetState} variant="secondary" />
        <Button label="Continue Anyway" onClick={onAcknowledgeRecurringWarning} variant="primary" />
      </XStack>
    ))
    .with('selectConfirmationStatus', () => null)
    .with('incompleteReason', () => (
      <XStack justifyContent="end">
        <Button
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className="px-xl bg-[#F1F3F6] border-borderPrimary"
          onClick={onSkipNotDoneReason}
          variant="secondary"
        >
          Skip
        </Button>
      </XStack>
    ))
    .with('taskReview', () => (
      <Button disabled={isSubmitDisabled} isLoading={isSubmitLoading} onClick={handleSubmitClick}>
        Close task
      </Button>
    ))
    .exhaustive();

  if (!task) return null;

  return (
    <Modal
      Footer={
        footerButtons ? (
          <YStack>
            <Divider />
            <YStack className="p-md">{footerButtons}</YStack>
          </YStack>
        ) : null
      }
      Header={
        <YStack>
          <XStack className="items-center justify-between p-md">
            {canGoBack ? (
              <Icon name="IconChevronLeft" onClick={handleBackClick} size={32} />
            ) : (
              <YStack className="w-md" />
            )}
            <YStack flex={1}>
              <Text textAlign="center" type="h4">
                {title}
              </Text>
            </YStack>
            <Icon className="cursor-pointer hover:bg-slate-100 rounded p-1" name="IconX" onClick={closeAndResetState} />
          </XStack>
          <Divider />
        </YStack>
      }
      isOpen={isOpen}
      onClose={closeAndResetState}
      padding="none"
      size="md"
      title=""
    >
      {centerContent}
    </Modal>
  );
};
